import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import TooltipWithIcon from '../../shared/components/custom/TooltipWithIcon.jsx';
import TimeframeSelector from './components/TimeframeSelector';
import StatsCards from './components/StatsCards';
import ScreenTable from './components/ScreenTable';
import UserChart from './components/UserChart';
import UsersByOsChart from './components/UsersByOsChart';
import NewVsReturningChart from './components/NewVsReturningChart';
import HourlyUsersChart from './components/HourlyUsersChart';
import DateRangePicker from './components/DateRangePicker';
import { partial } from '../../util/translation/translation';

const MAX_HOURLY_RANGE_DAYS = 8;

import { useUserLoginData } from './hooks/useUserLoginData';
import { useUserAvgLoginStats } from './hooks/useUserAvgLoginStats';

export function UserLoginStats() {
  const a = partial('Analytics');

  const [timeframe, setTimeframe] = useState('7days');
  const [userDataType, setUserDataType] = useState('all');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [chartView, setChartView] = useState('sessions');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  const {
    loginData,
    rawData,
    totalSessions,
    sessionsPerUser,
    activeUsers,
    deviceData,
    screenData,
    osByDateData,
    newVsReturningData,
    hourlyData,
    isLoading: sessionDataLoading,
    fetchUserLoginStats,
    fetchCustomDateStats,
    processData,
  } = useUserLoginData();
  const {
    averageEngagementTimePerUser,
    userAvgDataLoading,
    fetchUserAvgLoginStats,
    fetchCustomAvgDateStats,
  } = useUserAvgLoginStats();

  const rangeDays = Math.ceil(
    (dateRange.end - dateRange.start) / (24 * 60 * 60 * 1000)
  );
  const canUseHourly =
    timeframe === '7days' ||
    (timeframe === 'Custom' && rangeDays <= MAX_HOURLY_RANGE_DAYS);

  const formatDate = (date) => {
    return date
      .toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '/');
  };

  const handleTimeframeClick = async (tf) => {
    if (tf === 'Custom') {
      // Only open the picker here - switching the active pill and
      // fetching happens in handleDatePickerApply once the user actually
      // applies a range. Otherwise cancelling left "Custom" highlighted as
      // active while the chart/date label still showed the previous
      // timeframe's data.
      setShowDatePicker(true);
      return;
    }
    setTimeframe(tf);
    setUserDataType('all');
    setShowDatePicker(false);

    const today = new Date();
    const end = new Date(today);
    end.setDate(today.getDate() - 1);
    let start = new Date();

    if (tf === '7days') {
      start = new Date(end);
      start.setDate(end.getDate() - 7);
    } else if (tf === '1month') {
      start = new Date(end - 30 * 24 * 60 * 60 * 1000);
    } else if (tf === '3months') {
      start = new Date(end - 90 * 24 * 60 * 60 * 1000);
    }

    setDateRange({ start, end });

    const willSupportHourly = tf === '7days';
    if (chartView === 'hourly' && !willSupportHourly) setChartView('sessions');
    const granularity =
      chartView === 'hourly' && willSupportHourly ? 'hour' : undefined;

    await fetchUserLoginStats(tf, granularity);
    await fetchUserAvgLoginStats(tf);
  };

  const handleDatePickerApply = async (startDate, endDate) => {
    setShowDatePicker(false);
    setTimeframe('Custom');
    setUserDataType('all');
    setDateRange({ start: startDate, end: endDate });

    const days = Math.ceil((endDate - startDate) / (24 * 60 * 60 * 1000));
    const willSupportHourly = days <= MAX_HOURLY_RANGE_DAYS;
    if (chartView === 'hourly' && !willSupportHourly) setChartView('sessions');
    const granularity =
      chartView === 'hourly' && willSupportHourly ? 'hour' : undefined;

    await fetchCustomDateStats(startDate, endDate, granularity);
    await fetchCustomAvgDateStats(startDate, endDate);
  };

  const handleUserDataTypeChange = (type) => {
    setUserDataType(type);
    processData(rawData, type);
  };

  const handleChartViewChange = async (view) => {
    setChartView(view);
    if (view === 'hourly' && canUseHourly) {
      if (timeframe === 'Custom') {
        await fetchCustomDateStats(dateRange.start, dateRange.end, 'hour');
      } else {
        await fetchUserLoginStats(timeframe, 'hour');
      }
    }
  };

  useEffect(() => {
    fetchUserLoginStats('7days');
    fetchUserAvgLoginStats('7days');
  }, []);

  return (
    <div className="pb-5" style={{ minHeight: '100vh' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <TimeframeSelector
            timeframe={timeframe}
            onTimeframeChange={handleTimeframeClick}
          />
          <div>
            {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
          </div>
        </div>

        <Row className="mb-4">
          <Col xs={12} md={7} lg={5}>
            <StatsCards
              totalSessions={totalSessions}
              sessionsPerUser={sessionsPerUser}
              averageEngagementTime={
                averageEngagementTimePerUser.formatted_period_avg_engagement_per_user
              }
              sessionDataLoading={sessionDataLoading}
              userAvgDataLoading={userAvgDataLoading}
              loginData={loginData}
              devices={deviceData}
              peakActiveUsers={activeUsers}
              sumOfDailyAveragesEngagement={
                averageEngagementTimePerUser.formatted_sum_of_daily_averages
              }
            />
          </Col>

          <Col xs={12} md={5} lg={7} className="mt-3 mt-lg-0">
            <ScreenTable
              screenData={screenData}
              isLoading={sessionDataLoading}
              loginData={loginData}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardHeader className="bg-white">
                <div className="d-flex justify-content-between align-items-center">
                  <CardTitle
                    tag="h6"
                    className="mb-0"
                    style={{ fontSize: '18px', fontWeight: 'bold' }}
                  >
                    {a('userLoginStatistics') + ' '}
                    <TooltipWithIcon
                      id="no-access-view-tooltip"
                      text={a('loginStatsInfo')}
                    />
                  </CardTitle>
                  {chartView === 'sessions' && (
                    <UserDataTypeSelector
                      userDataType={userDataType}
                      onUserDataTypeChange={handleUserDataTypeChange}
                    />
                  )}
                </div>
                <ChartViewSelector
                  chartView={chartView}
                  onChartViewChange={handleChartViewChange}
                  canUseHourly={canUseHourly}
                />
              </CardHeader>
              <CardBody>
                <DateRangePicker
                  isOpen={showDatePicker}
                  onCancel={() => setShowDatePicker(false)}
                  onApply={handleDatePickerApply}
                />

                {chartView === 'sessions' && (
                  <UserChart
                    loginData={loginData}
                    userDataType={userDataType}
                    isLoading={sessionDataLoading}
                  />
                )}
                {chartView === 'byOs' && (
                  <UsersByOsChart
                    data={osByDateData}
                    isLoading={sessionDataLoading}
                  />
                )}
                {chartView === 'newVsReturning' && (
                  <NewVsReturningChart
                    data={newVsReturningData}
                    isLoading={sessionDataLoading}
                  />
                )}
                {chartView === 'hourly' && (
                  <HourlyUsersChart
                    data={hourlyData}
                    isLoading={sessionDataLoading}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

function ChartViewSelector({ chartView, onChartViewChange, canUseHourly }) {
  const a = partial('Analytics');

  return (
    <Nav pills className="mt-2">
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onChartViewChange('sessions')}
          active={chartView === 'sessions'}
        >
          {a('sessions')}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onChartViewChange('byOs')}
          active={chartView === 'byOs'}
        >
          {a('byPlatform')}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onChartViewChange('newVsReturning')}
          active={chartView === 'newVsReturning'}
        >
          {a('newVsReturning')}
        </NavLink>
      </NavItem>
      {canUseHourly && (
        <NavItem>
          <NavLink
            role="button"
            onClick={() => onChartViewChange('hourly')}
            active={chartView === 'hourly'}
          >
            {a('hourly')}
          </NavLink>
        </NavItem>
      )}
    </Nav>
  );
}

function UserDataTypeSelector({ userDataType, onUserDataTypeChange }) {
  const a = partial('Analytics');

  return (
    <Nav pills>
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onUserDataTypeChange('all')}
          active={userDataType === 'all'}
        >
          {a('allSessions')}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onUserDataTypeChange('unique')}
          active={userDataType === 'unique'}
        >
          {a('uniqueUsers')}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          role="button"
          onClick={() => onUserDataTypeChange('both')}
          active={userDataType === 'both'}
        >
          {a('both')}
        </NavLink>
      </NavItem>
    </Nav>
  );
}
